import { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/common/Button';
import './HomePage.css';

const SHOWDOWN_EXAMPLE = `Urshifu-Rapid-Strike @ Choice Scarf
Ability: Unseen Fist
Tera Type: Steel
EVs: 100 HP / 156 Atk / 4 Def / 76 SpD / 172 Spe
Adamant Nature
- Surging Strikes
- Close Combat
- Aqua Jet
- U-turn

Chien-Pao @ Focus Sash
Ability: Sword of Ruin
Tera Type: Ghost
EVs: 252 Atk / 4 Def / 252 Spe
Adamant Nature
- Protect
- Icicle Crash
- Sucker Punch
- Sacred Sword

Raging Bolt @ Leftovers
Ability: Protosynthesis
Level: 50
Tera Type: Fairy
EVs: 188 HP / 140 Def / 100 SpA / 4 SpD / 76 Spe
Modest Nature
IVs: 20 Atk
- Thunderclap
- Dragon Pulse
- Calm Mind
- Protect

Landorus @ Life Orb
Ability: Sheer Force
Level: 50
Tera Type: Poison
EVs: 132 HP / 12 Def / 116 SpA / 4 SpD / 244 Spe
Modest Nature
IVs: 0 Atk
- Earth Power
- Sludge Bomb
- Substitute
- Protect

Incineroar @ Safety Goggles
Ability: Intimidate
Level: 50
Tera Type: Ghost
EVs: 252 HP / 100 Def / 156 SpD
Careful Nature
- Flare Blitz
- Parting Shot
- Knock Off
- Fake Out

Rillaboom @ Assault Vest
Ability: Grassy Surge
Tera Type: Fire
EVs: 252 HP / 36 Atk / 76 Def / 140 SpD / 4 Spe
Adamant Nature
- Wood Hammer
- Grassy Glide
- U-turn
- Fake Out`;

const FORMAT_CARDS = [
  {
    icon: '🔄',
    title: 'Rondes Suisses',
    paragraphs: [
      'Dans un tournoi avec rondes suisses, les joueurs affrontent des joueurs ayant le même nombre de victoires qu\'eux (sauf exceptions).',
      'Le nombre de rondes suisses varie en fonction du nombre de participants et selon les choix de l\'organisateur. Si il y a un nombre important de joueurs, les rondes suisses peuvent se dérouler sur deux jours. Vous le saurez au moment de vous inscrire.',
      'Si vous perdez, ce n\'est pas forcément fini. En revanche, la plupart du temps, perdre plus de 2 fois signifie ne pas pouvoir accéder à la phase à élimination directe.',
    ],
  },
  {
    icon: '🏆',
    title: 'Phase à élimination directe - Top Cut',
    paragraphs: [
      'Lorsque les rondes suisses se terminent, si vous finissez avec 2 défaites ou moins (ou si vous finissez dans le top 8/16 en fonction du nombre de participants et selon les choix de l\'organisateur), vous êtes qualifiés pour une phase à élimination directe qui déterminera le vainqueur du tournoi.',
      'Une défaite signifie l\'élimination.',
    ],
  },
  {
    icon: '🎮',
    title: 'Matchs en 2 manches gagnantes - Bo3',
    paragraphs: [
      'En général, chaque match est joué avec un maximum de 3 manches. Le joueur ayant remporté 2 de ces manches remporte le match.',
      'Si précisé (avec la mention de Bo1 pour la phase de rondes suisses), cela signifie que les matchs seront joués en une seule manche : un seul match est nécessaire pour déterminer le vainqueur.',
      'Par défaut, tous les matchs (Rondes Suisses et Top Cut) se jouent en Bo3.',
    ],
  },
  {
    icon: '📋',
    title: 'Open Team List',
    paragraphs: [
      'Lors des matchs de tournoi, chaque joueur a accès à une liste simplifiée de l\'équipe de l\'adversaire.',
    ],
    list: {
      included: ['Les Pokémon', 'Leur objet', 'Leurs attaques', 'Leur talent'],
      excluded: ['Les répartitions de stats', 'La nature'],
    },
  },
];

const REGISTRATION_STEPS = [
  {
    text: <>Créez un compte sur <a href="https://play.limitlesstcg.com/signup" target="_blank" rel="noopener noreferrer">Limitless</a>, la plateforme principale de tournois online organisés par la communauté.</>,
    image: '/assets/limitless_register.png',
  },
  {
    text: <>Cherchez un tournoi parmi ceux présents sur la plateforme, ou rejoignez directement les tournois en lien avec Champions Collective.</>,
    image: '/assets/limitless_find.png',
    note: 'Nous vous conseillons de rejoindre le serveur Discord lié au tournoi pour être notifié du lancement du tournoi/d\'une ronde et contacter le staff en cas de questions ou soucis.',
    ctas: [
      { label: 'Voir les tournois Champions Collective', to: '/tournois' },
      { label: 'Voir les tournois Limitless', href: 'https://play.limitlesstcg.com/tournaments/upcoming?game=VGC&format=M-A&platform=SWITCH&type=online', external: true },
    ],
  },
  {
    text: <>Inscrivez-vous, et votre équipe ! Dans un tournoi, une seule équipe est autorisée par joueur. Utilisez un export Showdown pour inscrire votre équipe.</>,
    image: '/assets/limitless_sumbit.png',
    showdownExample: true,
  },
  {
    text: <>N'oubliez pas de <strong>Check-In</strong> avant que le tournoi commence, si cela est précisé.</>,
  },
  {
    text: <>Allez dans la section <strong>Pairings</strong> du tournoi et cherchez votre pseudo. Vous serez redirigé vers un chat avec votre adversaire, c'est ici que&nbsp;:</>,
    bullets: [
      'Vous cliquez sur le bouton Check-In pour confirmer votre présence à ce match (IMPORTANT)',
      'Vous décidez qui va créer la room',
      'Le créateur de la room donne le code',
      'Vous donnez votre pseudo in-game si besoin',
      'Vous consultez l\'Open Team List de votre adversaire pendant le match',
    ],
    image: '/assets/limitless_pairings.png',
  },
  {
    text: <>Pour créer une room, allez dans l'option des <strong>Combats Privés</strong>.</>,
    image: '/assets/champions_privatebattle.jpg',
  },
  {
    text: <>Sélectionnez l'option de création de room.</>,
    image: '/assets/champions_makeroom.jpg',
  },
  {
    text: <>Sélectionnez les règles demandées par le tournoi que vous avez rejoint.</>,
    image: '/assets/champions_selectrules.jpg',
  },
  {
    text: <>Dans le menu de la room, partagez son code. Si autorisé, vous pouvez aussi partager le code à d'autres personnes pour le mode spectateur. Lorsque tout le monde est prêt, lancez le combat.</>,
    image: '/assets/champions_startbattle.jpg',
  },
  {
    text: <>Lorsque le match est terminé, dans la page Limitless où vous discutez avec votre adversaire, cliquez sur le bouton du vainqueur du match (ou de la manche en Bo3). Après vérification, vous pouvez attendre les pairings de la prochaine ronde.</>,
  },
  {
    text: <>Si vous êtes qualifié à la phase à élimination directe (Top Cut), cliquez sur le match où votre pseudo est présent pour arriver à la page du chat.</>,
    image: '/assets/limitless_topcut.png',
  },
];

export default function HomePage() {
  return (
    <div className="home">
      {/* Hero */}
      <section className="home__hero">
        <div className="home__hero-overlay" />
        <div className="container home__hero-inner">
          <h1 className="home__hero-title honk-title">Champions Collective</h1>
          <p className="home__hero-subtitle chiron-goround-tc-body">
            Participez à des tournois organisés par la communauté
          </p>
          <div className="home__hero-actions">
            <Link to="/tournois">
              <Button variant="primary" size="lg">Voir nos tournois</Button>
            </Link>
            <Link to="/classement">
              <Button variant="secondary" size="lg">Classement</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Tournament Format */}
      <section className="home__format section">
        <div className="container">
          <h2 className="home__section-title staatliches-regular">
            Comment fonctionnent les tournois sur Pokémon Champions ?
          </h2>
          <p className="home__format-intro chiron-goround-tc-body">
            Les tournois organisés officiellement ou par la communauté respectent généralement toujours le même format.
          </p>
          <div className="home__format-grid">
            {FORMAT_CARDS.map((card) => (
              <div className="home__format-card" key={card.title}>
                <span className="home__format-icon">{card.icon}</span>
                <h3 className="home__format-card-title staatliches-regular">{card.title}</h3>
                {card.paragraphs.map((p, i) => (
                  <p key={i} className="chiron-goround-tc-body">{p}</p>
                ))}
                {card.list && (
                  <div className="home__format-card-lists">
                    <div>
                      <p className="chiron-goround-tc-bold">Cette liste contient :</p>
                      <ul className="chiron-goround-tc-body">
                        {card.list.included.map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    </div>
                    <div>
                      <p className="chiron-goround-tc-bold">Elle ne contient pas :</p>
                      <ul className="chiron-goround-tc-body">
                        {card.list.excluded.map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Guide */}
      <section className="home__guide section">
        <div className="container">
          <h2 className="home__section-title staatliches-regular">Comment s'inscrire à un tournoi ?</h2>
          <ol className="home__steps">
            {REGISTRATION_STEPS.map((step, i) => (
              <RegistrationStep key={i} index={i + 1} step={step} />
            ))}
          </ol>
        </div>
      </section>

      {/* Help */}
      <section className="home__help section">
        <div className="container home__help-inner">
          <h2 className="home__section-title staatliches-regular">Besoin d'assistance ?</h2>
          <p className="chiron-goround-tc-body">
            N'hésitez pas à faire appel aux organisateurs/staff du tournoi où vous participez.
          </p>
        </div>
      </section>

      {/* Drop */}
      <section className="home__drop section">
        <div className="container home__drop-inner">
          <h2 className="home__section-title staatliches-regular">Vous ne pouvez plus participer à un tournoi ?</h2>
          <p className="chiron-goround-tc-body">
            Si ce tournoi n'a pas encore commencé, n'oubliez pas de vous désinscrire.
          </p>
          <p className="chiron-goround-tc-body">
            Si ce tournoi a commencé, contactez l'organisateur ou le staff de ce tournoi afin de leur notifier votre souhait de Drop le tournoi.
          </p>
        </div>
      </section>
    </div>
  );
}

function RegistrationStep({ index, step }) {
  const [showExample, setShowExample] = useState(false);

  return (
    <li className="home__step">
      <div className="home__step-number staatliches-regular">{index}</div>
      <div className="home__step-content">
        <p className="home__step-text chiron-goround-tc-body">{step.text}</p>

        {step.note && (
          <p className="home__step-note chiron-goround-tc-body">{step.note}</p>
        )}

        {step.bullets && (
          <ul className="home__step-bullets chiron-goround-tc-body">
            {step.bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        )}

        {step.showdownExample && (
          <div className="home__showdown">
            <button
              className="home__showdown-toggle staatliches-regular"
              onClick={() => setShowExample(!showExample)}
              aria-expanded={showExample}
            >
              {showExample ? 'Masquer l\'exemple' : 'Voir un exemple d\'export Showdown'}
              <span className={`home__showdown-chevron ${showExample ? 'open' : ''}`}>▼</span>
            </button>
            {showExample && (
              <pre className="home__showdown-code"><code>{SHOWDOWN_EXAMPLE}</code></pre>
            )}
          </div>
        )}

        {step.ctas && (
          <div className="home__step-ctas">
            {step.ctas.map((cta, i) =>
              cta.external ? (
                <a key={i} href={cta.href} target="_blank" rel="noopener noreferrer" className="home__step-cta">
                  <Button variant={i === 0 ? 'primary' : 'secondary'} size="sm">{cta.label}</Button>
                </a>
              ) : (
                <Link key={i} to={cta.to} className="home__step-cta">
                  <Button variant={i === 0 ? 'primary' : 'secondary'} size="sm">{cta.label}</Button>
                </Link>
              )
            )}
          </div>
        )}

        {step.cta && !step.ctas && (
          <Link to={step.cta.to} className="home__step-cta">
            <Button variant="primary" size="sm">{step.cta.label}</Button>
          </Link>
        )}

        {step.image && (
          <div className="home__step-image-wrapper">
            <img src={step.image} alt={`Étape ${index}`} className="home__step-image" loading="lazy" />
          </div>
        )}
      </div>
    </li>
  );
}
